import moment from 'moment';
import { calculateSubtotal, nightsBetweenDates } from './RentableUtils';
export default class CampaignManager {
    constructor(campaigns, itemMap, specMap) {
        this.campaignInConjunctionWithOthers = true;
        this.campaigns = campaigns || [];
        this.itemMap = itemMap || {};
        this.specMap = specMap || {};
    }

    setItemMap(itemMap) {
        this.itemMap = itemMap;
    }

    setSpecMap(specMap) {
        this.specMap = specMap;
    }
    
    getValidCampaigns(levelId, serviceId, cartItems, orderItems, start, end) {
        this.campaignInConjunctionWithOthers = true;
        let anyCampaignApplied = false;
        if (cartItems && !orderItems) {
            orderItems = this.getOrderItemsFromCartItems(cartItems, start, end);
        }

        return this.campaigns.sort((a, b) => {
            if (a.enabled_at !== b.enabled_at) {
                return new Date(b.enabled_at).getTime() - new Date(a.enabled_at).getTime();
            }
            return b.id - a.id;
        }).filter(campaign => {
            if (this.campaignInConjunctionWithOthers) {
                const valid = this.validateCampaign(campaign, levelId, serviceId, orderItems, start, end);
                if (!valid) {
                    return false;
                }
                if (!anyCampaignApplied) {
                    this.campaignInConjunctionWithOthers = campaign.in_conjunction_with_others;
                    anyCampaignApplied = true;
                    return true;
                } else {
                    return campaign.in_conjunction_with_others;
                }
            } else {
                return false;
            }
        });
    }

    validateCampaign(campaign, levelId, serviceId, orderItems, start, end) {
        const condition = campaign.condition;

        if (condition.customer_level_ids.length) {
            const levelMatched = !!condition.customer_level_ids.find(id => id === levelId);
            if (!levelMatched) {
                return false;
            }
        }

        if (condition.service_ids.length) {
            const serviceMatched = !!condition.service_ids.find(id => id === serviceId);
            if (!serviceMatched) {
                return false;
            }
        }

        const limitedBuyables = condition.buyables;
        if (limitedBuyables.length) {
            const limitedOrderItems = this.getLimitedOrderItems(limitedBuyables, orderItems);
            let validBuybales = false;
            if (campaign.all_items_required) {
                validBuybales = limitedOrderItems.length >= limitedBuyables.length;
            } else {
                validBuybales = limitedOrderItems.length > 0;
            }
            if (!validBuybales) {
                return false;
            }
        }

        if (condition.target && !this.checkCampaignTarget(condition, orderItems, moment(start), moment(end))) {
            return false;
        }

        return true;
    }

    getOrderItemsFromCartItems(cartItems, start, end) { // only service, item, spec items
        return cartItems.filter(cartItem => !!cartItem.serviceId || !!cartItem.itemId || !!cartItem.specId).map(cartItem => {
            if (cartItem.serviceId) {
                return {
                    orderable_type: 'App\\Models\\Service',
                    orderable_id: cartItem.serviceId,
                    count: +cartItem.count,
                    total: cartItem.total,
                }
            } else if (cartItem.specId) {
                const item = this.itemMap[cartItem.itemId];
                const spec = this.specMap[cartItem.itemId]?.find(spec => spec.id == cartItem.specId);
                const orderItem = {
                    orderable_type: 'App\\Models\\ProviderItemSpec',
                    orderable_id: cartItem.specId,
                    count: +cartItem.count,
                    total: spec.price * +cartItem.count,
                }
                if (item.is_renting && start && end) {
                    if (spec.renting_charge_per_day !== null) {
                        const startMoment = moment(start).startOf('day');
                        const endMoment = moment(end).startOf('day');
                        orderItem.total = calculateSubtotal(spec, +cartItem.count, startMoment, endMoment);
                    } else if (spec.renting_charge_per_booking !== null) {
                        orderItem.total = parseInt(spec.renting_charge_per_booking) * +cartItem.count;
                    } else {
                        orderItem.total = 0;
                    }
                }

                return orderItem;
            } else if (cartItem.itemId) {
                const item = this.itemMap[cartItem.itemId];
                const orderItem = {
                    orderable_type: 'App\\Models\\ProviderItem',
                    orderable_id: cartItem.itemId,
                    count: +cartItem.count,
                    total: item.price * +cartItem.count,
                }
                if (item.is_renting) {
                    if (item.renting_charge_per_day !== null) {
                        const startMoment = moment(start).startOf('day');
                        const endMoment = moment(end).startOf('day');
                        orderItem.total = calculateSubtotal(item, +cartItem.count, startMoment, endMoment);
                    } else if (item.renting_charge_per_booking !== null) {
                        orderItem.total = parseInt(item.renting_charge_per_booking) * +cartItem.count;
                    } else {
                        orderItem.total = 0;
                    }
                }

                return orderItem;
            }
        })
    }

    checkCampaignTarget(condition, orderItems, start, end) {
        const target = condition.target;
        const limitedBuyables = condition.buyables;
        const limitedOrderItems = this.getLimitedOrderItems(limitedBuyables, orderItems);
        const buyableAndServiceTotal = this.getItemTotal(orderItems);
        const itemOrSpecCount = this.getItemOrSpecCount(orderItems);
        const limitedItemTotal = this.getItemTotal(limitedOrderItems);
        const limitedItemCount = this.getItemOrSpecCount(limitedOrderItems);

        switch (target.type) {
            case 'MIN_AMOUNT':
            case 'EVERY_AMOUNT':
                if (limitedBuyables.length) {
                    return limitedItemTotal >= target.threshold;
                } else {
                    return buyableAndServiceTotal >= target.threshold;
                }
            case 'MIN_COUNT':
            case 'EVERY_COUNT':
                if (limitedBuyables.length) {
                    return limitedItemCount >= target.threshold;
                } else {
                    return itemOrSpecCount >= target.threshold;
                }
            case 'MIN_NIGHTS':
            case 'EVERY_NIGHTS':
                if (!start || !end) {
                    return false;
                } else {
                    return nightsBetweenDates(start, end) >= target.threshold;
                }
            default:
                return true;
        }
    }

    getLimitedOrderItems(limitedBuyables, orderItems) {
        const limitedOrderItems = orderItems.filter(orderItem => {
            const matched = limitedBuyables.find(buyable => {
                if (buyable.spec_id) {
                    return orderItem.orderable_type === 'App\\Models\\ProviderItemSpec' && orderItem.orderable_id === buyable.spec_id;
                } else {
                    return orderItem.orderable_type === 'App\\Models\\ProviderItem' && orderItem.orderable_id === buyable.item_id;
                }
            })
            return !!matched;
        });

        return limitedOrderItems;
    }

    getItemOrSpecCount(orderItems) {
        return orderItems.reduce((count, item) => {
            if (item.orderable_type === 'App\\Models\\ProviderItem' || item.orderable_type === 'App\\Models\\ProviderItemSpec') {
                return count + +item.count;
            } else {
                return count;
            }
        }, 0);
    }

    getItemTotal(orderItems) {
        return orderItems.reduce((total, item) => {
            return total + +item.total;
        }, 0);
    }
}
